import React, { useState } from 'react';

function GalleryGrid({ images, nsfwPreference, onImageClick }) {
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [showBatchActions, setShowBatchActions] = useState(false);

  const toggleSelection = (e, imageId) => {
    e.stopPropagation();
    const newSelected = new Set(selectedImages);
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId);
    } else {
      newSelected.add(imageId);
    }
    setSelectedImages(newSelected);
    setShowBatchActions(newSelected.size > 0);
  };

  const selectAll = () => {
    const allIds = new Set(images.map(img => img.id));
    setSelectedImages(allIds);
    setShowBatchActions(true);
  };

  const deselectAll = () => {
    setSelectedImages(new Set());
    setShowBatchActions(false);
  };

  const handleBatchDelete = async () => {
    if (!confirm(`Delete ${selectedImages.size} images?`)) return;
    
    try {
      for (const imageId of selectedImages) {
        await fetch(`/api/images/${imageId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
      }
      setSelectedImages(new Set());
      setShowBatchActions(false);
      window.location.reload();
    } catch (err) {
      console.error('Batch delete failed:', err);
    }
  };

  const handleBatchDownload = () => {
    selectedImages.forEach(imageId => {
      const image = images.find(img => img.id === imageId);
      if (image) {
        const link = document.createElement('a');
        link.href = `/api/images/${imageId}/download`;
        link.download = image.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb > 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
  };

  const shouldBlur = (image) => {
    if (!image.is_nsfw) return false;
    if (nsfwPreference === 'show') return false;
    if (nsfwPreference === 'hide') return true;
    return true; // blur
  };

  return (
    <div className="gallery-grid-container">
      {showBatchActions && (
        <div className="batch-actions">
          <span>{selectedImages.size} selected</span>
          <div className="batch-buttons">
            <button className="btn-batch" onClick={selectAll}>Select All</button>
            <button className="btn-batch" onClick={deselectAll}>Deselect</button>
            <button className="btn-batch btn-download" onClick={handleBatchDownload}>Download</button>
            <button className="btn-batch btn-delete" onClick={handleBatchDelete}>Delete</button>
          </div>
        </div>
      )}

      <div className="gallery-grid">
        {images.map((image, index) => {
          const isSelected = selectedImages.has(image.id);
          const blurImage = shouldBlur(image);
          const isHidden = image.is_nsfw && nsfwPreference === 'hide';

          if (isHidden) return null;

          return (
            <div 
              key={image.id}
              className={`gallery-item ${isSelected ? 'selected' : ''}`}
              onClick={() => onImageClick(image, index)}
            >
              <div className="image-checkbox">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => toggleSelection(e, image.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              
              <div className={`image-wrapper ${blurImage ? 'nsfw-blur' : ''}`}>
                <img 
                  src={`/api/images/${image.id}/thumbnail`}
                  alt={image.title || image.filename}
                  loading="lazy"
                />
                {blurImage && <div className="nsfw-overlay">NSFW</div>}
              </div>
              
              <div className="image-info">
                <p className="image-title" title={image.title || image.filename}>
                  {image.title || image.filename}
                </p>
                <div className="image-meta">
                  <span>{image.width}x{image.height}</span>
                  <span>{formatFileSize(image.size_bytes)}</span>
                </div>
                {image.artist && (
                  <p className="image-artist">{image.artist}</p>
                )}
                {image.is_compressed && (
                  <span className="compressed-badge">compressed</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default GalleryGrid;